import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import api from '../services/api';
import { Banner } from './ui';

/**
 * ExpenseEntryCard – form for a single expense entry.
 *
 * Props:
 *   entry         – existing ExpenseEntry record (or null for new)
 *   rateCodes     – array of expense-type rate codes (RateType=Expense)
 *   customers     – array of customers
 *   isEditable    – boolean (false if on a submitted report)
 *   onSave        – callback(data)
 *   onDelete      – callback()
 *   onJobChanged  – callback(jobId, jobTaskId, rateSheetCode) – called when inline job picker changes
 *   onPrev        – go to previous entry
 *   onNext        – go to next entry
 *   entryIndex    – current entry index
 *   entryCount    – total entries
 *   entries       – all entries for prev/next labels
 */
const ExpenseEntryCard = forwardRef(function ExpenseEntryCard({
  entry,
  rateCodes,
  customers = [],
  isEditable = true,
  onSave,
  onDelete,
  onJobChanged,
  onPrev,
  onNext,
  showNav = false,
  entryIndex,
  entryCount,
  entries,
  onSplit = null,
  topExpenseTypes = [],
  recentJobs = [],
}, ref) {
  const [entryDate, setEntryDate] = useState('');
  const [rateCodeId, setRateCodeId] = useState('');
  const [rateSheetCode, setRateSheetCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [description, setDescription] = useState('');
  const [nonBillable, setNonBillable] = useState(false);
  const [nonReimbursable, setNonReimbursable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Inline job picker state
  const [freeform, setFreeform] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [jobTaskId, setJobTaskId] = useState('');
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  // Freeform state
  const [allJobs, setAllJobs] = useState([]);
  const [loadingAllJobs, setLoadingAllJobs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFreeformJob, setSelectedFreeformJob] = useState(null);
  const [freeformTasks, setFreeformTasks] = useState([]);
  const [freeformTaskId, setFreeformTaskId] = useState('');
  const [loadingFreeformTasks, setLoadingFreeformTasks] = useState(false);

  // Ref to track latest form values for auto-save without stale closures
  const formRef = useRef({});
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  useEffect(() => {
    formRef.current = { entryDate, rateSheetCode, rateCodeId, quantity, unitPrice, totalPrice, description, nonBillable, nonReimbursable, jobId, jobTaskId };
  }, [entryDate, rateSheetCode, rateCodeId, quantity, unitPrice, totalPrice, description, nonBillable, nonReimbursable, jobId, jobTaskId]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { savingRef.current = saving; }, [saving]);

  // Auto-save: saves current form state if dirty, callable from parent via ref
  const saveIfDirty = useCallback(async () => {
    if (!dirtyRef.current || savingRef.current || !entry) return;
    const f = formRef.current;
    setSaving(true);
    savingRef.current = true;
    setSaveError(null);
    try {
      await onSave({
        entryDate: f.entryDate || null,
        rateSheetCode: f.rateSheetCode || null,
        rateCodeId: f.rateCodeId || null,
        quantity: f.quantity ? parseFloat(f.quantity) : null,
        unitPrice: f.unitPrice ? parseFloat(f.unitPrice) : null,
        totalPrice: f.totalPrice ? parseFloat(f.totalPrice) : null,
        description: f.description,
        nonBillable: f.nonBillable,
        nonReimbursable: f.nonReimbursable,
        jobId: f.jobId || null,
        jobTaskId: f.jobTaskId || null,
      });
      setDirty(false);
      dirtyRef.current = false;
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }, [entry, onSave]);

  // Expose saveIfDirty to parent via ref
  useImperativeHandle(ref, () => ({ saveIfDirty }), [saveIfDirty]);

  // Auto-save on blur from any form field within the card
  const handleCardBlur = useCallback((e) => {
    // Only auto-save if focus is leaving the card entirely, or moving to nav/receipt/tab buttons
    // Use a short delay to let the new focus target settle
    setTimeout(() => {
      if (dirtyRef.current && !savingRef.current && entry) {
        saveIfDirty();
      }
    }, 150);
  }, [saveIfDirty, entry]);

  // Filter rate codes by the entry's ratesheet (and rate=0 for imported entries)
  const isImported = !!entry?.Imported;
  const filteredRateCodes = useMemo(() => {
    const sheet = rateSheetCode || entry?.RateSheetCode;
    if (!sheet) return [];
    let codes = rateCodes.filter(rc => rc.RateSheetCode === sheet);
    if (isImported) codes = codes.filter(rc => parseFloat(rc.Rate) === 0);
    return codes;
  }, [rateCodes, rateSheetCode, entry?.RateSheetCode, isImported]);

  // Top 15 most-used expense types for this ratesheet
  const topFilteredExpenseTypes = useMemo(() => {
    if (!topExpenseTypes || topExpenseTypes.length === 0 || filteredRateCodes.length === 0) return [];
    const rankMap = new Map();
    topExpenseTypes.forEach((t, i) => {
      rankMap.set(`${t.RateSheetCode}|${t.RateCodeID}`, i);
    });
    return filteredRateCodes
      .filter(rc => rankMap.has(`${rc.RateSheetCode}|${rc.RateCodeID}`))
      .sort((a, b) => {
        const aRank = rankMap.get(`${a.RateSheetCode}|${a.RateCodeID}`);
        const bRank = rankMap.get(`${b.RateSheetCode}|${b.RateCodeID}`);
        return aRank - bRank;
      });
  }, [filteredRateCodes, topExpenseTypes]);

  // Current rate code info
  const selectedRateCode = rateCodes.find(
    rc => rc.RateCodeID === rateCodeId && rc.RateSheetCode === rateSheetCode
  );
  const billingRate = selectedRateCode ? parseFloat(selectedRateCode.Rate) : 0;
  const hasRate = billingRate !== 0;

  // Imported (non-split) entries should have their amount locked
  const isImportedAmountLocked = !!entry?.Imported && !entry?.HeaderEntryID;

  // Sync form with entry
  useEffect(() => {
    if (entry) {
      setEntryDate(entry.EntryDate ? entry.EntryDate.split('T')[0] : '');
      setRateCodeId(entry.RateCodeID || '');
      setRateSheetCode(entry.RateSheetCode || '');
      setQuantity(entry.Quantity != null ? String(entry.Quantity) : '');
      setUnitPrice(entry.UnitPrice != null ? String(parseFloat(entry.UnitPrice).toFixed(2)) : '');
      setTotalPrice(entry.TotalPrice != null ? String(parseFloat(entry.TotalPrice).toFixed(2)) : '');
      setDescription(entry.Description || '');
      setNonBillable(!!entry.NonBillable);
      setNonReimbursable(!!entry.NonReimbursable);
      // Sync job picker state
      setJobId(entry.JobID || '');
      setJobTaskId(entry.JobTaskID || '');
      setCustomerId(entry.CustomerID || '');
      // Reset freeform state
      setFreeform(false);
      setSelectedFreeformJob(null);
      setSearchQuery('');
      setFreeformTaskId('');
      setFreeformTasks([]);

      // If entry has a job but no ratesheet code, look it up from the job
      if (entry.JobID && !entry.RateSheetCode) {
        api.getJobs().then(jobList => {
          const job = jobList.find(j => j.JobID === entry.JobID);
          if (job?.RateSheetCode) {
            setRateSheetCode(job.RateSheetCode);
          }
        }).catch(err => console.error('Failed to lookup job ratesheet:', err));
      }
    } else {
      const today = new Date().toISOString().split('T')[0];
      setEntryDate(today);
      setRateCodeId('');
      setRateSheetCode('');
      setQuantity('');
      setUnitPrice('');
      setTotalPrice('');
      setDescription('');
      setNonBillable(false);
      setNonReimbursable(false);
      setJobId('');
      setJobTaskId('');
      setCustomerId('');
      setFreeform(false);
      setSelectedFreeformJob(null);
      setSearchQuery('');
    }
    setDirty(false);
    setSaveError(null);
  }, [entry]);

  // Load jobs when customer changes (dropdown mode)
  useEffect(() => {
    if (freeform) return;
    if (!customerId) {
      setJobs([]);
      return;
    }
    setLoadingJobs(true);
    api.getJobs(customerId)
      .then((j) => setJobs(j))
      .finally(() => setLoadingJobs(false));
  }, [customerId, freeform]);

  // Load tasks when job changes (dropdown mode)
  useEffect(() => {
    if (freeform) return;
    if (!jobId) {
      setTasks([]);
      setJobTaskId('');
      return;
    }
    setLoadingTasks(true);
    api.getJobTasks(jobId, { taskCode: 'Expense' })
      .then((t) => {
        setTasks(t);
        if (t.length === 1 && !jobTaskId) {
          setJobTaskId(t[0].JobTaskID);
        }
      })
      .finally(() => setLoadingTasks(false));
  }, [jobId, freeform]); // eslint-disable-line

  // Load all jobs when freeform is enabled
  useEffect(() => {
    if (!freeform || allJobs.length > 0) return;
    setLoadingAllJobs(true);
    api.getJobs()
      .then((j) => setAllJobs(j))
      .finally(() => setLoadingAllJobs(false));
  }, [freeform, allJobs.length]);

  // Freeform: Load tasks when a job is selected
  useEffect(() => {
    if (!freeform || !selectedFreeformJob) return;
    setLoadingFreeformTasks(true);
    setFreeformTaskId('');
    api.getJobTasks(selectedFreeformJob.JobID, { taskCode: 'Expense' })
      .then((t) => {
        setFreeformTasks(t);
        if (t.length === 1) {
          setFreeformTaskId(t[0].JobTaskID);
          // Also set jobId/jobTaskId and rateSheetCode so the expense type dropdown works
          setJobId(selectedFreeformJob.JobID);
          setJobTaskId(t[0].JobTaskID);
          if (selectedFreeformJob.RateSheetCode) {
            setRateSheetCode(selectedFreeformJob.RateSheetCode);
          }
          setDirty(true);
          if (onJobChanged) onJobChanged(selectedFreeformJob.JobID, t[0].JobTaskID, selectedFreeformJob.RateSheetCode || '');
        }
      })
      .finally(() => setLoadingFreeformTasks(false));
  }, [freeform, selectedFreeformJob]); // eslint-disable-line

  // Freeform: filtered jobs (show recent jobs when search is empty)
  const filteredJobs = useMemo(() => {
    if (!freeform) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return recentJobs.slice(0, 30);
    const custMap = {};
    for (const c of customers) {
      custMap[c.CustomerID] = c.CustomerName?.toLowerCase() || '';
    }
    return allJobs.filter((j) => {
      const custName = custMap[j.CustomerID] || '';
      return `${j.JobID} ${j.JobName} ${custName}`.toLowerCase().includes(q);
    }).slice(0, 30);
  }, [freeform, allJobs, searchQuery, customers, recentJobs]);

  // When job selection changes, notify parent and update ratesheet
  const handleJobSelection = useCallback(async (newJobId, newJobTaskId) => {
    // Look up the job to get its RateSheetCode
    let sheet = rateSheetCode;
    if (newJobId) {
      const jobList = freeform ? allJobs : jobs;
      let job = jobList.find(j => j.JobID === newJobId) || recentJobs.find(j => j.JobID === newJobId);
      if (!job) {
        const fetched = await api.getJobs();
        job = fetched.find(j => j.JobID === newJobId);
      }
      if (job?.RateSheetCode) {
        sheet = job.RateSheetCode;
        setRateSheetCode(sheet);
      }
    }
    setDirty(true);
    if (onJobChanged) onJobChanged(newJobId, newJobTaskId, sheet);
  }, [freeform, allJobs, jobs, recentJobs, rateSheetCode, onJobChanged]);

  // Dropdown mode: handle job change
  const handleDropdownJobChange = useCallback((newJobId) => {
    setJobId(newJobId);
    setJobTaskId('');
    setTasks([]);
    if (newJobId) {
      const job = jobs.find(j => j.JobID === newJobId) || recentJobs.find(j => j.JobID === newJobId);
      if (job?.RateSheetCode) {
        setRateSheetCode(job.RateSheetCode);
        setDirty(true);
      }
    }
  }, [jobs, recentJobs]);

  // Dropdown mode: handle task change
  const handleDropdownTaskChange = useCallback((newTaskId) => {
    setJobTaskId(newTaskId);
    if (jobId && newTaskId) {
      handleJobSelection(jobId, newTaskId);
    }
  }, [jobId, handleJobSelection]);

  // Freeform: handle task change
  const handleFreeformTaskChange = useCallback((newTaskId) => {
    setFreeformTaskId(newTaskId);
    if (selectedFreeformJob && newTaskId) {
      setJobId(selectedFreeformJob.JobID);
      setJobTaskId(newTaskId);
      handleJobSelection(selectedFreeformJob.JobID, newTaskId);
    }
  }, [selectedFreeformJob, handleJobSelection]);

  // When rate code changes, update pricing logic
  const handleRateCodeChange = useCallback((value) => {
    const parts = value.split('|');
    const newRateSheet = parts[0] || '';
    const newRateCodeId = parts[1] || '';
    setRateSheetCode(newRateSheet);
    setRateCodeId(newRateCodeId);
    setDirty(true);

    const rc = rateCodes.find(r => r.RateCodeID === newRateCodeId && r.RateSheetCode === newRateSheet);
    const rate = rc ? parseFloat(rc.Rate) : 0;

    if (rate !== 0) {
      setUnitPrice(String(rate));
      const qty = parseFloat(quantity) || 0;
      if (qty) setTotalPrice(String((qty * rate).toFixed(2)));
    } else {
      setQuantity('1');
      setUnitPrice('');
    }
  }, [rateCodes, quantity]);

  // When quantity changes and there's a billing rate, recalculate total
  const handleQuantityChange = useCallback((val) => {
    setQuantity(val);
    setDirty(true);
    if (hasRate) {
      const qty = parseFloat(val) || 0;
      setTotalPrice(String((qty * billingRate).toFixed(2)));
    }
  }, [hasRate, billingRate]);

  // When total changes and rate=0, sync unit price
  const handleTotalChange = useCallback((val) => {
    setTotalPrice(val);
    setUnitPrice(val);
    setQuantity('1');
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        entryDate: entryDate || null,
        rateSheetCode: rateSheetCode || null,
        rateCodeId: rateCodeId || null,
        quantity: quantity ? parseFloat(quantity) : null,
        unitPrice: unitPrice ? parseFloat(unitPrice) : null,
        totalPrice: totalPrice ? parseFloat(totalPrice) : null,
        description,
        nonBillable,
        nonReimbursable,
        jobId: jobId || null,
        jobTaskId: jobTaskId || null,
      });
      setDirty(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this expense entry?')) return;
    setSaving(true);
    try {
      await onDelete();
    } finally {
      setSaving(false);
    }
  };

  // Labels for prev/next navigation
  const prevEntry = entryIndex > 0 ? entries[entryIndex - 1] : null;
  const nextEntry = entryIndex < entryCount - 1 ? entries[entryIndex + 1] : null;
  const entryLabel = (e) => {
    if (!e) return '';
    let rawDesc = e.Description || '';
    // Strip "Credit Card Charge by: " prefix for cleaner display
    rawDesc = rawDesc.replace(/^Credit Card Charge by:\s*/i, '');
    const desc = rawDesc ? rawDesc.substring(0, 30) : 'No desc';
    const amt = e.TotalPrice != null ? `$${parseFloat(e.TotalPrice).toFixed(2)}` : '$0.00';
    return `${desc} (${amt})`;
  };

  // Build the rate code select value as "rateSheetCode|rateCodeId"
  const rateCodeValue = rateSheetCode && rateCodeId ? `${rateSheetCode}|${rateCodeId}` : '';

  // Has a job been selected on this entry?
  const hasJob = !!(entry?.JobID || jobId);

  return (
    <div className="entry-card" style={{ margin: '8px 16px' }} onBlur={handleCardBlur}>
      {/* Inline Job/Task Selection */}
      {isEditable && (
        <div style={{ marginBottom: '12px', padding: '10px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', margin: 0 }}>Job / Task</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', margin: 0 }}>
              <input
                type="checkbox"
                checked={freeform}
                onChange={(e) => {
                  setFreeform(e.target.checked);
                  setSelectedFreeformJob(null);
                  setFreeformTaskId('');
                  setFreeformTasks([]);
                  setSearchQuery('');
                }}
              />
              Search
            </label>
          </div>

          {!freeform ? (
            <>
              <div className="form-group" style={{ marginBottom: '6px' }}>
                <label style={{ fontSize: '12px' }}>Customer</label>
                <select
                  className="form-control"
                  value={customerId}
                  onChange={(e) => {
                    setCustomerId(e.target.value);
                    setJobId('');
                    setJobTaskId('');
                    setTasks([]);
                  }}
                  style={{ fontSize: '13px' }}
                >
                  <option value="">Select customer...</option>
                  {customers.map((c) => (
                    <option key={c.CustomerID} value={c.CustomerID}>
                      {c.CustomerName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '6px' }}>
                <label style={{ fontSize: '12px' }}>Job</label>
                <select
                  className="form-control"
                  value={jobId}
                  onChange={(e) => {
                    const newJobId = e.target.value;
                    if (!customerId && newJobId) {
                      const rj = recentJobs.find(j => j.JobID === newJobId);
                      if (rj) setCustomerId(rj.CustomerID);
                    }
                    handleDropdownJobChange(newJobId);
                  }}
                  disabled={(!customerId && recentJobs.length === 0) || loadingJobs}
                  style={{ fontSize: '13px' }}
                >
                  <option value="">{loadingJobs ? 'Loading...' : 'Select job...'}</option>
                  {(() => {
                    if (!customerId && recentJobs.length > 0) {
                      return (
                        <optgroup label="Recent Jobs">
                          {recentJobs.map((j) => (
                            <option key={`recent-${j.JobID}`} value={j.JobID}>{j.JobID} - {j.JobName} ({j.CustomerName})</option>
                          ))}
                        </optgroup>
                      );
                    }
                    const recentInCustomer = recentJobs.filter(rj => rj.CustomerID === customerId && jobs.some(j => j.JobID === rj.JobID));
                    if (recentInCustomer.length > 0) {
                      return (
                        <>
                          <optgroup label="Recent Jobs">
                            {recentInCustomer.map((j) => (
                              <option key={`recent-${j.JobID}`} value={j.JobID}>{j.JobID} - {j.JobName}</option>
                            ))}
                          </optgroup>
                          <optgroup label="All Jobs">
                            {jobs.map((j) => (
                              <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>
                            ))}
                          </optgroup>
                        </>
                      );
                    }
                    return jobs.map((j) => (
                      <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>
                    ));
                  })()}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <label style={{ fontSize: '12px' }}>Task</label>
                {tasks.length === 1 ? (
                  <div className="form-control" style={{ background: 'white', fontSize: '13px' }}>
                    {tasks[0].JobTaskID} – {tasks[0].TaskName}
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={jobTaskId}
                    onChange={(e) => handleDropdownTaskChange(e.target.value)}
                    disabled={!jobId || loadingTasks}
                    style={{ fontSize: '13px' }}
                  >
                    <option value="">{loadingTasks ? 'Loading...' : 'Select task...'}</option>
                    {tasks.map((t) => (
                      <option key={t.JobTaskID} value={t.JobTaskID}>
                        {t.JobTaskID} – {t.TaskName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="form-group" style={{ marginBottom: '6px' }}>
                <label style={{ fontSize: '12px' }}>Search by job, customer, or ID</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder={loadingAllJobs ? 'Loading jobs...' : 'Type to search...'}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedFreeformJob(null);
                    setFreeformTaskId('');
                    setFreeformTasks([]);
                    setJobId('');
                    setJobTaskId('');
                  }}
                  disabled={loadingAllJobs}
                  style={{ fontSize: '13px' }}
                />
                {filteredJobs.length > 0 && !selectedFreeformJob && (
                  <div style={{
                    maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--gray-200)',
                    borderRadius: '6px', marginTop: '4px', background: 'white',
                  }}>
                    {!searchQuery.trim() && filteredJobs.length > 0 && (
                      <div style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', background: 'var(--gray-50)', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1px solid var(--gray-200)' }}>
                        Recent Jobs
                      </div>
                    )}
                    {filteredJobs.map((j) => {
                      const custName = customers.find((c) => c.CustomerID === j.CustomerID)?.CustomerName || '';
                      return (
                        <div
                          key={j.JobID}
                          onClick={() => {
                            setSelectedFreeformJob(j);
                            setSearchQuery(`${j.JobID} - ${j.JobName}`);
                          }}
                          style={{
                            padding: '6px 10px', cursor: 'pointer', fontSize: '13px',
                            borderBottom: '1px solid var(--gray-100)',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ fontWeight: 500 }}>{j.JobID} - {j.JobName}</div>
                          {custName && <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{custName}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedFreeformJob && (
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
                    Selected: {selectedFreeformJob.JobID} - {selectedFreeformJob.JobName}
                    <button
                      onClick={() => {
                        setSelectedFreeformJob(null);
                        setSearchQuery('');
                        setFreeformTaskId('');
                        setFreeformTasks([]);
                        setJobId('');
                        setJobTaskId('');
                      }}
                      style={{ marginLeft: '8px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <label style={{ fontSize: '12px' }}>Task</label>
                {freeformTasks.length === 1 ? (
                  <div className="form-control" style={{ background: 'white', fontSize: '13px' }}>
                    {freeformTasks[0].JobTaskID} – {freeformTasks[0].TaskName}
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={freeformTaskId}
                    onChange={(e) => handleFreeformTaskChange(e.target.value)}
                    disabled={!selectedFreeformJob || loadingFreeformTasks}
                    style={{ fontSize: '13px' }}
                  >
                    <option value="">{loadingFreeformTasks ? 'Loading...' : 'Select task...'}</option>
                    {freeformTasks.map((t) => (
                      <option key={t.JobTaskID} value={t.JobTaskID}>
                        {t.JobTaskID} – {t.TaskName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}

          {/* Show current job info if already assigned */}
          {entry?.JobID && !dirty && (
            <div style={{ marginTop: '6px', padding: '6px 8px', background: 'white', borderRadius: '4px', fontSize: '12px', color: 'var(--gray-600)' }}>
              {entry.CustomerName && <span style={{ fontWeight: 500 }}>{entry.CustomerName} &rsaquo; </span>}
              {entry.JobID} - {entry.JobName || ''} &rsaquo; {entry.JobTaskID} – {entry.TaskName || ''}
            </div>
          )}
        </div>
      )}

      {/* Show job info (read-only) when not editable */}
      {!isEditable && entry && entry.JobID && (
        <div className="entry-card-header">
          <div className="entry-card-info">
            {entry.CustomerName && <div className="entry-card-customer">{entry.CustomerName}</div>}
            <div className="entry-card-job">{entry.JobID} - {entry.JobName || ''}</div>
            <div className="entry-card-task">{entry.JobTaskID} – {entry.TaskName || ''}</div>
          </div>
        </div>
      )}

      {/* Date */}
      <div className="form-group">
        <label>Date</label>
        <input
          type="date"
          className="form-control"
          value={entryDate}
          onChange={(e) => { setEntryDate(e.target.value); setDirty(true); }}
          disabled={!isEditable || isImported}
          title={isImported ? 'The date of an auto-imported expense cannot be changed.' : undefined}
          style={isImported ? { background: 'var(--gray-100)', color: 'var(--gray-500)' } : undefined}
        />
      </div>

      {/* Expense Type (rate code) – filtered by job's ratesheet */}
      <div className="form-group">
        <label>Expense Type</label>
        <select
          className="form-control"
          value={rateCodeValue}
          onChange={(e) => handleRateCodeChange(e.target.value)}
          disabled={!isEditable || (!hasJob && filteredRateCodes.length === 0)}
        >
          <option value="">{!hasJob ? 'Select a job first...' : 'Select expense type...'}</option>
          {topFilteredExpenseTypes.length > 0 && (
            <optgroup label="Most Used">
              {topFilteredExpenseTypes.map((rc) => (
                <option key={`top-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                  {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label={topFilteredExpenseTypes.length > 0 ? 'All Expense Types' : undefined}>
            {filteredRateCodes.map((rc) => (
              <option key={`all-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Quantity + Unit Price (only if billing rate <> 0) */}
      {hasRate && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <div className="entry-form-row" style={{ flex: 1 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Quantity</label>
              <input
                type="number"
                className="form-control"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                step="0.01"
                placeholder="0"
                disabled={!isEditable || isImportedAmountLocked}
                inputMode="decimal"
                style={isImportedAmountLocked ? { background: 'var(--gray-100)', color: 'var(--gray-500)' } : undefined}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Unit Price</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                <input
                  type="number"
                  className="form-control"
                  value={unitPrice}
                  readOnly
                  disabled
                  style={{ background: 'var(--gray-50)', paddingLeft: '20px' }}
                />
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Total</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                <input
                  type="number"
                  className="form-control"
                  value={totalPrice}
                  readOnly
                  disabled
                  style={{ background: 'var(--gray-50)', paddingLeft: '20px' }}
                />
              </div>
            </div>
          </div>
          {onSplit && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={onSplit}
              style={{ whiteSpace: 'nowrap', marginBottom: '12px' }}
            >
              Split
            </button>
          )}
        </div>
      )}

      {/* Amount only (if billing rate = 0 or no rate code selected yet) */}
      {!hasRate && (
        <div className="form-group">
          <label>Amount</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', fontSize: '14px', pointerEvents: 'none' }}>$</span>
              <input
                type="text"
                className="form-control"
                value={totalPrice}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.\-]/g, '');
                  handleTotalChange(val);
                }}
                onBlur={() => {
                  const num = parseFloat(totalPrice);
                  if (!isNaN(num)) {
                    setTotalPrice(num.toFixed(2));
                    setUnitPrice(num.toFixed(2));
                  }
                }}
                placeholder="0.00"
                disabled={!isEditable || isImportedAmountLocked}
                inputMode="decimal"
                style={isImportedAmountLocked ? { paddingLeft: '20px', background: 'var(--gray-100)', color: 'var(--gray-500)' } : { paddingLeft: '20px' }}
              />
            </div>
            {onSplit && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={onSplit}
                style={{ whiteSpace: 'nowrap' }}
              >
                Split
              </button>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <div className="form-group">
        <label>Description</label>
        <textarea
          className="form-control"
          value={description}
          maxLength={250}
          onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
          placeholder="Describe the expense..."
          disabled={!isEditable}
          rows={2}
        />
      </div>

      {/* Footer: Non-billable + Save/Delete */}
      <div className="entry-footer-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              id="expNonBillable"
              checked={nonBillable}
              onChange={(e) => { setNonBillable(e.target.checked); setDirty(true); }}
              disabled={!isEditable}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="expNonBillable" style={{ margin: 0, fontSize: '13px' }}>
              Non-Billable
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              id="expNonReimbursable"
              checked={nonReimbursable}
              onChange={(e) => { setNonReimbursable(e.target.checked); setDirty(true); }}
              disabled={!isEditable || !!entry?.Imported}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="expNonReimbursable" style={{ margin: 0, fontSize: '13px', color: entry?.Imported ? '#999' : undefined }}>
              Non-Reimbursable
            </label>
          </div>
        </div>
        {isEditable && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {dirty && !saving && (
              <span style={{ fontSize: '11px', color: 'var(--warning)' }}>unsaved</span>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : entry ? 'Update' : 'Save'}
            </button>
            {entry && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {saveError && <Banner kind="err">{saveError}</Banner>}

      {/* Nav arrows (shown only when the user enables "Show Next/Previous Expense") */}
      {showNav && entryCount > 1 && (
        <div className="entry-nav-row" style={{ marginTop: '10px' }}>
          <button
            className="entry-nav-btn"
            onClick={onPrev}
            disabled={entryIndex === 0}
            type="button"
          >&#9650; {prevEntry ? entryLabel(prevEntry) : 'Up'}</button>
          <button
            className="entry-nav-btn"
            onClick={onNext}
            disabled={entryIndex >= entryCount - 1}
            type="button"
          >&#9660; {nextEntry ? entryLabel(nextEntry) : 'Down'}</button>
        </div>
      )}

      {entryCount > 0 && (
        <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
          {entryIndex + 1} / {entryCount}
        </div>
      )}
    </div>
  );
});

export default ExpenseEntryCard;
